/**
 * Agent connection form helpers.
 *
 * Pure helpers for the agent connection form: Foundry model-type detection,
 * the contextual info message shown per model type, and submit-readiness
 * validation. Extracted from `AgentConnectionForm.tsx` so the workflow
 * connection-creation panel, the edit dialog, and the form itself share one
 * implementation — and one validation source of truth — instead of duplicating
 * the per-type branch logic.
 */
import type { AgentConnectionFormValues, AgentModelType } from '@/types/agentConnection';

/**
 * Check if an agent model type is a Foundry type (V1 or V2).
 */
export const isFoundryModelType = (modelType: string | undefined): boolean =>
  modelType === 'FoundryAgentService' || modelType === 'FoundryAgentServiceV2';

/**
 * Get the contextual info message for an agent connection form,
 * suitable for display in a tooltip next to the section title.
 */
export const getAgentConnectionInfoMessage = (agentModelType: AgentModelType): string => {
  switch (agentModelType) {
    case 'AzureOpenAI':
      return 'Select your Azure OpenAI resource. Credentials will be auto-filled.';
    case 'FoundryAgentService':
    case 'FoundryAgentServiceV2':
      return 'Select your AI Foundry project. Authentication uses Automation project managed identity.';
    case 'APIMGenAIGateway':
      return 'API Management gateway selection coming soon.';
    case 'V1ChatCompletionsService':
      return 'Enter your OpenAI-compatible endpoint and key.';
    case 'GitHubCopilot':
      return 'Connect your GitHub account to use GitHub Copilot models.';
    default:
      return '';
  }
};

/**
 * Validate an agent connection form for submission readiness.
 *
 * Source of truth for the ConnectionCreationPanel "Create connection" button
 * gate. It branches Foundry types through isFoundryModelType() so every Foundry
 * variant (V1 and V2) is covered — the button gate previously used a literal
 * switch that only handled FoundryAgentService, leaving FoundryAgentServiceV2
 * connections permanently un-creatable. The submit path keeps its own per-field
 * error messaging but routes Foundry the same way, so both stay consistent.
 */
export const isAgentConnectionFormValid = (
  agentModelType: AgentModelType,
  values: AgentConnectionFormValues,
): boolean => {
  if (!values.displayName) return false;
  if (agentModelType === 'AzureOpenAI') {
    return !!(values.subscriptionId && values.accountId && values.endpoint);
  }
  if (isFoundryModelType(agentModelType)) {
    return !!(values.subscriptionId && values.projectId && values.endpoint);
  }
  if (agentModelType === 'V1ChatCompletionsService' || agentModelType === 'APIMGenAIGateway') {
    return !!(values.endpoint && values.apiKey);
  }
  return false;
};
